/* eslint-disable react/jsx-key */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
 Checkbox, Switch } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { Course, CourseLevelsEnum } from 'src/@types/Course';
import { InstructorType } from 'src/sections/dashboard/instructors/types';
import { Track } from 'src/@types/tracks';
import { Category } from 'src/@types/Category';
import { UploadAvatar, UploadMultiFile } from 'src/components/upload';
import { Package } from 'src/@types/package';
import { apiErrorText } from 'src/utils/apiErrorText';
import {
  CourseFormValues,
  ORDER_HELPER_TEXT,
  addKeyword,
  buildCourseCreate,
  buildCoursePatch,
  courseFormValues,
  courseSchema,
  courseValidationKeys,
  removeKeyword,
} from './courseFormPayload';

// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentCourse?: Course;
  handleClose(): void;
};

export default function CourseNewEditForm({ isEdit, currentCourse, handleClose }: Props) {
  const [instructors, setInstructors] = useState<InstructorType[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPathTrack, setSelectedPathTrack] = useState<Track[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  // The values the form opened with (a deep copy): an edit sends only what
  // differs from them, and checks the API rules on those fields only.
  const defaultValues: CourseFormValues = useMemo(
    () => courseFormValues(currentCourse),
    [currentCourse]
  );
  const initialRef = useRef<CourseFormValues>(JSON.parse(JSON.stringify(defaultValues)));
  const resolver = useCallback<Resolver<CourseFormValues>>(
    (formValues, context, options) =>
      yupResolver(courseSchema(courseValidationKeys(formValues, initialRef.current, isEdit)))(
        formValues,
        context,
        options
      ),
    [isEdit]
  );
   const uploadImage = async (file: File) => {
     const form = new FormData();
     form.append('file', file);
     const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
     return response.data;
   };

  const methods = useForm<CourseFormValues>({
    resolver,
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    trigger,
    setValue,
    formState: { isSubmitting, errors },
  } = methods;
  const values = watch();

  useEffect(() => {
    initialRef.current = JSON.parse(JSON.stringify(defaultValues));
    if (isEdit && currentCourse) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentCourse]);

  const onSubmit = async (data: CourseFormValues) => {
    try {
      if (currentCourse) {
        // Only the changed fields: never `sections` (lessons are saved in the
        // course view), `rating` or `duration`.
        const body = buildCoursePatch(data, initialRef.current);
        if (!Object.keys(body).length) {
          enqueueSnackbar('No changes to save', { variant: 'info' });
          handleClose();
          return;
        }
        await axiosInstance.patch(APIS.COURSES.PATCH(currentCourse.id as string), body);
      } else await axiosInstance.post(APIS.COURSES.ADD, buildCourseCreate(data));
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      enqueueSnackbar(
        apiErrorText(err, !isEdit ? 'Unable to create the course' : 'Unable to save the course'),
        { variant: 'error' }
      );
    }
  };
  // react-hook-form calls THIS when submit is blocked by validation. The
  // keywords field is a custom control with no ref to focus, so a failed
  // validation used to no-op silently ("Save Changes does nothing"). Surface
  // the first validation error so the admin knows what to fix.
  const onInvalid = (formErrors: any) => {
    const first: any = Object.values(formErrors || {})[0];
    const msg =
      (first && (first.message as string)) ||
      'Please fill in all required fields before saving.';
    enqueueSnackbar(msg, { variant: 'error' });
  };
  const getListingOptions = async () => {
    setInstructors((await axiosInstance.get(APIS.INSTRUCTORS.LIST)).data.data);
    setCategories((await axiosInstance.get(APIS.CATEGORIES.LIST)).data.data);
    setPackages((await axiosInstance.get(APIS.PACKAGES.LIST))?.data?.data);
   
  };
  useEffect(() => {
    if (currentCourse?.category) {
      getSelectedTrack(currentCourse.category._id as string,false);
    }
  }, [currentCourse,categories]);
  const onUploadCertificate = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.DOCUMENT, form);
    return response.data;
  };

  useEffect(() => {
    getListingOptions();
  }, []);
  const getSelectedTrack = (id: string,isChange:boolean) => {
    const path = categories.filter((item) => item._id == id);
    setSelectedPathTrack(path?.[0]?.tracks as Track[]);
    if (isChange)
    {
       setValue('track', '');
     }
  };

  return (
    <Stack height={'100%'}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Course' : ' Create Course'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <UploadAvatar
              label="Course Icon"
              accept={{ 'image/*': [] }}
              error={errors.icon ? true : false}
              file={values.icon}
              onDropAccepted={async (file) => {
                let response = await uploadImage(file[0]);
                setValue('icon', response?.url);
              }}
            />
            <UploadAvatar
              label="Course Image"
              accept={{ 'image/*': [] }}
              error={errors.image ? true : false}
              file={values.image}
              onDropAccepted={async (file) => {
                let response = await uploadImage(file[0]);
                setValue('image', response?.url);
              }}
            />
          </Stack>
          <Stack direction={'column'} spacing={5} overflow={'auto'}>
            <RHFTextField
              style={{ marginTop: '10px' }}
              fullWidth
              name="briefVideo"
              label="brief Video url"
            />

            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField fullWidth name="name" label="English Course Name" />
              <RHFTextField fullWidth name="name_ar" label="Arabic Course Name" />
              <RHFTextField fullWidth name="slug" label="Slug (URL)" />
              <RHFTextField fullWidth type="number" name="price" label="Price (EGP)" />
              <RHFTextField fullWidth type="number" name="price_usd" label="Price (USD)" />
              <RHFTextField
                fullWidth
                name="order"
                label="Order"
                helperText={ORDER_HELPER_TEXT}
                inputProps={{ inputMode: 'numeric', 'data-testid': 'course-order' }}
              />
              <RHFSelect
                onChange={(event) => {
                  setValue('category', event.target.value);
                  getSelectedTrack(event.target.value,true);
                }}
                name="category"
              >
                <option key={'default'} value={''} disabled>
                  {'Path'}
                </option>
                {categories?.map((option: Category) => (
                  <option key={option?._id} value={option?._id}>
                    {option.title}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="instructor">
                <option key={'default'} value={''} disabled>
                  {'Instructor'}
                </option>
                {instructors?.map((option: InstructorType) => (
                  <option key={option?._id} value={option?._id}>
                    {option.email ? `${option.name} — ${option.email}` : option.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="package">
                <option key={'default'} value={''} disabled>
                  {'package'}
                </option>
                {packages?.map((option: Package) => (
                  <option key={option?._id} value={option?._id}>
                    {option.name}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="track">
                <option key={'default'} value={''} disabled>
                  {'Track'}
                </option>
                {selectedPathTrack?.map((option: Track) => (
                  <option key={option?._id} value={option?._id}>
                    {option.title}
                  </option>
                ))}
              </RHFSelect>

              <FormControl>
                <FormLabel id="">Is Course Free</FormLabel>
                <FormControlLabel
                  checked={values.isFree}
                  onChange={(e: any) => {
                    console.log('Eee', e.target.checked);
                    setValue('isFree', !values.isFree);
                  }}
                  label="Free Course"
                  control={<Checkbox />}
                />
              </FormControl>

              <FormControl>
                <FormLabel id="">Visibility</FormLabel>
                <FormControlLabel
                  checked={!!values.active}
                  onChange={() => setValue('active', !values.active)}
                  label={values.active ? 'Published — students can see it' : 'Draft — hidden from students'}
                  control={<Switch />}
                />
              </FormControl>
            </Box>
            <RHFSelect name="level" label="Course Level" placeholder="Course Level">
              <option value="" />
              {Object.values(CourseLevelsEnum).map((value: string) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </RHFSelect>
            <UploadMultiFile
              error={
                errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl)
                  ? true
                  : false
              }
              disableControl={true}
              accept={{ 'application/pdf': ['.pdf'] }}
              files={values.certificateUrl ? [values.certificateUrl] : []}
              maxFiles={1}
              label={'Select Or Drop Certificate Url'}
              onDropAccepted={async (file) => {
                const response = await onUploadCertificate(file[0]);
                setValue('certificateUrl', response.url);
              }}
            />
            {errors.certificateUrl && (values.certificateUrl == '' || !values.certificateUrl) && (
              <FormHelperText error sx={{ px: 2, textAlign: 'start' }}>
                {errors.certificateUrl.message}
              </FormHelperText>
            )}

            <RHFTextField
              type="number"
              name="passScore"
              label="Pass score (%) — min final score to be certificate-eligible"
            />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField fullWidth name="seo_title" label="SEO Title (English)" />
              <RHFTextField fullWidth name="seo_title_ar" label="SEO Title (Arabic)" />
            </Box>
            <RHFTextField
              multiline
              minRows={2}
              maxRows={3}
              name="seo_description"
              label="SEO Description (English)"
            />
            <RHFTextField
              multiline
              minRows={2}
              maxRows={3}
              name="seo_description_ar"
              label="SEO Description (Arabic)"
            />
            <div>
              <Autocomplete
                multiple
                id="keywords"
                options={[]}
                value={values.keywords}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      onDelete={() => setValue('keywords', removeKeyword(values.keywords, index))}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errors.keywords && values.keywords.length < 1 ? true : false}
                    name={'keywords'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords', addKeyword(values.keywords, event?.target?.value));
                      }
                      // Removes the last keyword only from an empty input (it
                      // used to drop one on every key press while typing).
                      if (e.key === 'Backspace' && !event?.target?.value) {
                        setValue('keywords', (values.keywords || []).slice(0, -1));
                      }
                    }}
                    variant="outlined"
                    label="English Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
              {values.keywords.length == 0 && (
                <span style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '5px' }}>
                  {errors?.keywords?.message as any}
                </span>
              )}
            </div>
            <div>
              <Autocomplete
                multiple
                id="keywords_ar"
                options={[]}
                value={values?.keywords_ar??[]}
                freeSolo
                renderTags={(value: string[], getTagProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      onDelete={() => setValue('keywords_ar', removeKeyword(values.keywords_ar, index))}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={'keywords_ar'}
                    onKeyDown={(e) => {
                      let event = e as any;
                      if (e.key === 'Enter') {
                        setValue('keywords_ar', addKeyword(values.keywords_ar, event?.target?.value));
                      }
                      // Only from an empty input, and from the Arabic list (it
                      // used to pop the ENGLISH keywords into this field).
                      if (e.key === 'Backspace' && !event?.target?.value) {
                        setValue('keywords_ar', (values.keywords_ar || []).slice(0, -1));
                      }
                    }}
                    variant="outlined"
                    label="Arabic Keywords"
                    placeholder="Keywords"
                    maxRows={3}
                  />
                )}
              />
            </div>
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Course' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
